// LOCAL-DEV ONLY (devstack): Amplify v3 Storage has no endpoint config, so Storage.* always
// talks to real AWS S3. When REACT_APP_S3_LOCAL_ENDPOINT is set (https://s3-local.mhub.my,
// the devstack MinIO behind Caddy) AND the release stage is 'local', this drop-in implements
// the used subset of the Storage API (get/put/remove/list/configure) against MinIO with plain
// fetch — buckets are anonymous-public locally, so no signing is needed. Anywhere else — a
// deployed build, or a dev server sourcing .env.stage/.env.uat — LOCAL_S3_ENDPOINT is undefined
// and this re-exports the real Amplify Storage: zero behavior change. See src/utils/localStage.js
// for why the endpoint env alone is not a sufficient gate.
//
// Semantics mirrored from Amplify: keys live under a level prefix ('public/' by default,
// overridable via level/customPrefix — the SRB uploads use customPrefix {public: 'private/'});
// get() resolves to a URL string; put() resolves to {key}; list() to [{key,size,lastModified}]
// with keys relative to the prefix.
import { Storage as AmplifyStorage } from 'aws-amplify';

import { LOCAL_S3_ENDPOINT } from './localStage';

const ENDPOINT = LOCAL_S3_ENDPOINT;

const defaults = { bucket: process.env.REACT_APP_S3_BUCKET };

const prefixFor = (opts = {}) => {
  const level = opts.level || 'public';
  const custom = (opts.customPrefix || {})[level];
  return custom !== undefined ? custom : `${level}/`;
};

// Reject path-traversal / control chars, then percent-encode each path segment (preserving the '/'
// separators) so a key can't inject query/fragment/host into the URL or escape its prefix. Amplify
// signs the key server-side; here we build the URL ourselves, so this guard is on us.
const encodeKey = (key) => {
  if (typeof key !== 'string' || key === '') throw new Error('storage: key must be a non-empty string');
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(key)) throw new Error('storage: key contains control characters');
  if (key.split('/').some((seg) => seg === '..')) throw new Error('storage: key must not contain ".."');
  return key.split('/').map(encodeURIComponent).join('/');
};

// Resolve + validate the bucket name. We build URLs ourselves (Amplify would sign server-side),
// so an unvalidated bucket from opts could inject path/query/fragment/host into the URL.
const bucketFor = (opts = {}) => {
  const bucket = opts.bucket || defaults.bucket;
  if (!/^[a-zA-Z0-9._-]+$/.test(bucket || '')) throw new Error('storage: invalid bucket name');
  return bucket;
};

const urlFor = (key, opts = {}) => `${ENDPOINT}/${bucketFor(opts)}/${prefixFor(opts)}${encodeKey(key)}`;

const localStorage = {
  configure(cfg) {
    const bucket = cfg && cfg.AWSS3 && cfg.AWSS3.bucket;
    if (bucket) defaults.bucket = bucket;
  },
  async get(key, opts) {
    return urlFor(key, opts);
  },
  async put(key, object, opts = {}) {
    // Best-effort progress so callers (e.g. ShareSalesKit) don't stick at 0% locally. fetch()
    // has no upload-progress event, so we emit start/end around the request from the blob size.
    const total = (object && typeof object.size === 'number') ? object.size : 0;
    if (typeof opts.progressCallback === 'function') opts.progressCallback({ loaded: 0, total });
    const res = await fetch(urlFor(key, opts), {
      method: 'PUT',
      body: object,
      headers: opts.contentType ? { 'Content-Type': opts.contentType } : undefined,
    });
    if (!res.ok) throw new Error(`local S3 put failed: ${res.status}`);
    if (typeof opts.progressCallback === 'function') opts.progressCallback({ loaded: total, total });
    return { key };
  },
  async remove(key, opts) {
    const res = await fetch(urlFor(key, opts), { method: 'DELETE' });
    if (!res.ok && res.status !== 404) throw new Error(`local S3 remove failed: ${res.status}`);
    return {};
  },
  async list(prefix, opts = {}) {
    const bucket = bucketFor(opts);
    const full = `${prefixFor(opts)}${prefix || ''}`;
    const res = await fetch(`${ENDPOINT}/${bucket}?list-type=2&prefix=${encodeURIComponent(full)}`);
    if (!res.ok) throw new Error(`local S3 list failed: ${res.status}`);
    const xml = new DOMParser().parseFromString(await res.text(), 'text/xml');
    const strip = prefixFor(opts);
    return Array.from(xml.getElementsByTagName('Contents')).map((c) => {
      const el = (name) => (c.getElementsByTagName(name)[0] || {}).textContent || '';
      return {
        key: el('Key').startsWith(strip) ? el('Key').slice(strip.length) : el('Key'),
        size: Number(el('Size') || 0),
        lastModified: el('LastModified'),
        eTag: el('ETag'),
      };
    });
  },
};

// eslint-disable-next-line import/prefer-default-export
export const Storage = ENDPOINT ? localStorage : AmplifyStorage;
